import clsx from 'clsx';
import type { ReactNode } from 'react';
import type React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { RiSettings2Line, RiUserLine } from '@itandi/itandi-bb-ui';
import Styles from './SettingLayout.module.scss';
import { PATH as SETTING_ROOT_PATH } from '~/pages/vendors/settings';
import { PATH as PROFILE_PATH } from '~/pages/vendors/settings/profiles';
import { PATH as ACCOUNT_PATH } from '~/pages/vendors/settings/accounts';
import { PATH as VENDOR_USER_PATH } from '~/pages/vendors/settings/vendor_users';
import { PATH as COMPANY_SETTING_PATH } from '~/pages/vendors/settings/company_settings';
import { PATH as NOTIFICATION_SETTING } from '~/pages/vendors/settings/notifications';
import { PATH as VENDOR_PARTNERS_PATH } from '~/pages/vendors/settings/vendor_partners';
import { PATH as VENDOR_SURVEYS_PATH } from '~/pages/vendors/settings/surveys';
import { PATH as VENDOR_LINE_USER_ACCOUNTS_PATH } from '~/pages/vendors/settings/line_user_accounts';
import { useSessions } from '~/hooks/vendors/useSessions';

type Props = {
  children: ReactNode;
  spMenu?: boolean;
};

export const VendorSettingLayout: React.FC<Props> = ({
  children,
  spMenu = false
}: Props) => {
  const { data: session } = useSessions().index();
  const currentPath: string = useRouter().pathname;
  if (session === null) {
    return null;
  }
  const getActiveStyles = (targetPath: string): string => {
    if (currentPath === SETTING_ROOT_PATH) {
      if (session.admin) {
        return targetPath === ACCOUNT_PATH ? Styles.ActiveMenu : '';
      }
      return targetPath === PROFILE_PATH ? Styles.ActiveMenu : '';
    }
    if (currentPath.includes(targetPath)) {
      return Styles.ActiveMenu;
    }
    return '';
  };
  return (
    <div>
      <div className={Styles.SettingLayout}>
        <div
          className={clsx(Styles.SettingMenu, spMenu && Styles.SettingMenuSp)}
        >
          <h2 className={Styles.SettingHeader}>設定</h2>
          <p
            className={clsx(
              Styles.SettingSubHeader,
              session.admin ? Styles.Disabled : ''
            )}
          >
            <RiUserLine className={Styles.HeaderIcon} />
            個人設定
          </p>
          <ul className={Styles.SettingList}>
            <li
              className={clsx(
                Styles.SettingItem,
                getActiveStyles(PROFILE_PATH),
                session.admin ? Styles.Disabled : ''
              )}
            >
              <Link className={Styles.MenuLink} href={PROFILE_PATH}>
                プロフィール
              </Link>
            </li>
          </ul>
          <p className={Styles.SettingSubHeader}>
            <RiSettings2Line className={Styles.HeaderIcon} />
            施工会社アカウント設定
          </p>
          <ul>
            <li
              className={clsx(
                Styles.SettingItem,
                getActiveStyles(ACCOUNT_PATH)
              )}
            >
              <Link className={Styles.MenuLink} href={ACCOUNT_PATH}>
                施工会社アカウント
              </Link>
            </li>
            <li
              className={clsx(
                Styles.SettingItem,
                getActiveStyles(VENDOR_USER_PATH)
              )}
            >
              <Link className={Styles.MenuLink} href={VENDOR_USER_PATH}>
                担当者管理
              </Link>
            </li>
            <li
              className={clsx(
                Styles.SettingItem,
                getActiveStyles(COMPANY_SETTING_PATH)
              )}
            >
              <Link className={Styles.MenuLink} href={COMPANY_SETTING_PATH}>
                提携中の管理会社一覧
              </Link>
            </li>
            <li
              className={clsx(
                Styles.SettingItem,
                getActiveStyles(NOTIFICATION_SETTING)
              )}
            >
              <Link className={Styles.MenuLink} href={NOTIFICATION_SETTING}>
                通知設定
              </Link>
            </li>
            {session.vendorCompany.useLineNotification && (
              <li
                className={clsx(
                  Styles.SettingItem,
                  getActiveStyles(VENDOR_LINE_USER_ACCOUNTS_PATH)
                )}
              >
                <Link
                  className={Styles.MenuLink}
                  href={VENDOR_LINE_USER_ACCOUNTS_PATH}
                >
                  LINEアカウント設定
                </Link>
              </li>
            )}
            {session.vendorCompany.usePartner && (
              <li
                className={clsx(
                  Styles.SettingItem,
                  getActiveStyles(VENDOR_PARTNERS_PATH)
                )}
              >
                <Link className={Styles.MenuLink} href={VENDOR_PARTNERS_PATH}>
                  協力会社
                </Link>
              </li>
            )}
            {session.admin && (
              <li
                className={clsx(
                  Styles.SettingItem,
                  getActiveStyles(VENDOR_SURVEYS_PATH)
                )}
              >
                <Link className={Styles.MenuLink} href={VENDOR_SURVEYS_PATH}>
                  アンケート設定
                  <span className={Styles.AdminLabel}>Admin</span>
                </Link>
              </li>
            )}
          </ul>
        </div>
        <div className={clsx(Styles.SettingContent, spMenu && Styles.SpMenu)}>
          {children}
        </div>
      </div>
    </div>
  );
};
